import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Bot,
  ChevronRightIcon,
  Database,
  Workflow,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/shadcn/sheet";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/shadcn/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/shadcn/select";
import { Skeleton } from "@/components/shadcn/skeleton";
import { NoxusTooltip } from "@/components/UI/NoxusTooltip";
import { NoxusIcon } from "@/components/UI/NoxusIcon";
import { ConversationsService, ProvidersService } from "@/generated/sdk.gen";
import type {
  MessageCostBreakdown,
  MessageCostRow,
  ToolUsageLine,
} from "@/generated";
import { DATA_KEYS } from "@/utils/useRemoteData";
import { useSpotUser } from "@/utils/spotUser";
import { formatTime } from "@/utils/generic";
import { cn } from "@/lib/utils";

type SortMode = "time" | "time_desc" | "cost";

interface ConversationCostSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  conversationId: string | null;
  initialExpandedMessageId?: string | null;
}

function Chip({
  children,
  variant = "muted",
  className,
}: {
  children: ReactNode;
  variant?: "muted" | "platform";
  className?: string;
}) {
  return (
    <span
      className={cn(
        "rounded px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap",
        variant === "platform"
          ? "bg-primary/10 text-primary"
          : "bg-muted text-muted-foreground",
        className
      )}
    >
      {children}
    </span>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return <div className="mb-1 text-xs font-medium">{children}</div>;
}

// Tool names arrive backend-resolved ("run_workflow", "call_agent", fixed
// kb_* names) with the target's display name in `detail`. Map each kind to
// its icon and a readable label; humanize anything unrecognized.
const TOOL_LINE_META: Record<string, { label: string; icon: LucideIcon }> = {
  run_workflow: { label: "Run workflow", icon: Workflow },
  call_agent: { label: "Call agent", icon: Bot },
  kb_qa: { label: "Knowledge base search", icon: Database },
  kb_keyword: { label: "Keyword search", icon: Database },
  kb_semantic: { label: "Semantic search", icon: Database },
  kb_hybrid: { label: "Hybrid search", icon: Database },
  kb_tree: { label: "Browse knowledge base", icon: Database },
  kb_ls: { label: "List knowledge base files", icon: Database },
  get_file: { label: "Get file", icon: Database },
};

function toolLineMeta(tool: ToolUsageLine): {
  label: string;
  icon: LucideIcon;
  kindChip?: string;
} {
  const known = TOOL_LINE_META[tool.name];
  if (known) return known;
  // A workflow/agent line whose name is not a known tool is a sub-run that
  // ran without a tool call — its name is already the display name.
  if (tool.kind === "workflow")
    return { label: tool.name, icon: Workflow, kindChip: "workflow" };
  if (tool.kind === "agent")
    return { label: tool.name, icon: Bot, kindChip: "agent" };
  const humanized = tool.name.replace(/_/g, " ");
  return {
    label: humanized.charAt(0).toUpperCase() + humanized.slice(1),
    icon: Wrench,
  };
}

// Same structure as the provider details fields (DetailField): text-sm
// label over a text-sm medium value — plain text, no card chrome.
function Stat({
  title,
  value,
  footer,
}: {
  title: string;
  value: string;
  footer?: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <span className="text-sm font-medium">{title}</span>
      <span className="text-sm font-medium text-muted-foreground tabular-nums">
        {value}
      </span>
      {footer}
    </div>
  );
}

// Provider definitions carry the static logos, keyed on the no-llm provider
// type ("openai", "anthropic", …) — the same lookup the Models settings
// table builds.
function useProviderLogos(enabled: boolean): Record<string, string> {
  const { spotGroup } = useSpotUser();
  const { data } = useQuery({
    queryKey: [DATA_KEYS.PROVIDER_DEFINITIONS, "model", spotGroup.id],
    queryFn: () =>
      ProvidersService.listProviderDefinitions({
        path: { group_id: spotGroup.id },
        query: { category: "model" },
      }),
    enabled: enabled && !!spotGroup.id,
    staleTime: 5 * 60_000,
  });
  return useMemo(() => {
    const map: Record<string, string> = {};
    for (const d of (data?.data ?? []) as Array<{
      provider_logo?: string | null;
      provider_config?: Record<string, unknown>;
    }>) {
      const typeField = d.provider_config?.type as
        | { default?: unknown }
        | undefined;
      const t =
        typeof typeField?.default === "string" ? typeField.default : null;
      if (t && d.provider_logo) map[t] = d.provider_logo;
    }
    return map;
  }, [data]);
}

const tokensLabel = (input: number, cached: number, output: number): string => {
  const cachedNote = cached > 0 ? ` (${cached.toLocaleString()} cached)` : "";
  return `${input.toLocaleString()} input${cachedNote} · ${output.toLocaleString()} output tokens`;
};

const purposeLabel = (purpose: string): string => {
  const words = purpose.split("_").join(" ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const isEffectivelyZero = (cost: number): boolean =>
  cost.toFixed(4) === "0.0000";

const rowMeta = (row: MessageCostRow): string => {
  const calls = `${row.llm_calls} ${row.llm_calls === 1 ? "model call" : "model calls"}`;
  const tools =
    row.tool_calls > 0
      ? ` · ${row.tool_calls} ${row.tool_calls === 1 ? "tool" : "tools"}`
      : "";
  if (row.llm_calls === 0) {
    return `${formatTime(row.created_at)} · no billed calls`;
  }
  return `${formatTime(row.created_at)} · ${calls}${tools}`;
};

function ToolLine({ tool }: { tool: ToolUsageLine }) {
  const meta = toolLineMeta(tool);
  const isEstimate = tool.cost_is_estimate ?? true;
  const noxusIconKey =
    tool.name === "run_workflow" || tool.kind === "workflow"
      ? "workflow"
      : tool.name === "call_agent" || tool.kind === "agent"
        ? "coworker"
        : tool.name.startsWith("kb_")
          ? "kb"
          : undefined;
  const isNamedTarget = noxusIconKey !== undefined;
  const resolvedIcon = tool.icon || noxusIconKey;
  const primaryLabel = isNamedTarget && tool.detail ? tool.detail : meta.label;
  const labelTooltip = isNamedTarget && tool.detail ? meta.label : undefined;
  const priceText = `${isEstimate ? "~" : ""}$${(tool.cost ?? 0).toFixed(4)}`;
  const hasZeroSpend = isEffectivelyZero(tool.cost ?? 0);
  return (
    <div className="flex items-start justify-between gap-3 py-2">
      <div className="flex min-w-0 gap-2">
        {resolvedIcon ? (
          <NoxusIcon icon={resolvedIcon} size="smd" />
        ) : (
          <meta.icon
            size={16}
            strokeWidth={1.75}
            className="mt-0.5 shrink-0 text-muted-foreground"
          />
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            {labelTooltip ? (
              <NoxusTooltip content={labelTooltip} asChild>
                <span
                  className={cn(
                    "cursor-default truncate",
                    isEstimate ? "text-[11px]" : "text-sm"
                  )}
                >
                  {primaryLabel}
                </span>
              </NoxusTooltip>
            ) : (
              <span
                className={cn(
                  "truncate",
                  isEstimate ? "text-[11px]" : "text-sm"
                )}
              >
                {primaryLabel}
              </span>
            )}
            {meta.kindChip && <Chip variant="platform">{meta.kindChip}</Chip>}
            {tool.is_cached && <Chip className="font-normal">Cached</Chip>}
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            {tokensLabel(tool.input_tokens ?? 0, 0, tool.output_tokens ?? 0)}
          </div>
        </div>
      </div>
      <span
        className={cn(
          "shrink-0 pl-4 font-medium whitespace-nowrap tabular-nums",
          isEstimate ? "text-[11px]" : "text-sm",
          hasZeroSpend && "font-normal text-muted-foreground/60"
        )}
      >
        {priceText}
      </span>
    </div>
  );
}

function MessageBreakdownDetail({
  conversationId,
  messageId,
  providerLogos,
}: {
  conversationId: string;
  messageId: string;
  providerLogos: Record<string, string>;
}) {
  const { spotGroup } = useSpotUser();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["message-cost-breakdown", conversationId, messageId],
    queryFn: async () => {
      const response = await ConversationsService.getMessageCostBreakdown({
        path: {
          group_id: spotGroup.id,
          conversation_id: conversationId,
        },
        query: { message_id: messageId },
      });
      return response.data as MessageCostBreakdown;
    },
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 pt-3 pr-6 pb-3 pl-12">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="pt-3 pr-6 pb-3 pl-12 text-xs text-muted-foreground">
        No cost data available for this message.
      </div>
    );
  }

  // Additive spend reconciles to Total: direct model calls + embeddings +
  // workflow/agent runs (exact sub-run spend). The remaining tool lines are
  // marginal estimates already inside the model spend — shown, never added.
  const exactTools = data.tools.filter(
    (tool) => tool.cost_is_estimate === false
  );
  const estimateTools = data.tools.filter(
    (tool) => tool.cost_is_estimate !== false
  );
  const estimateTotal = estimateTools.reduce(
    (sum, tool) => sum + (tool.cost ?? 0),
    0
  );

  return (
    <div className="flex flex-col gap-3 pt-3 pr-6 pb-3 pl-12">
      <div>
        <SectionTitle>Model calls</SectionTitle>
        <div className="divide-y divide-border/60">
          {data.models.map((model, index) => {
            const logo = model.provider
              ? providerLogos[model.provider]
              : undefined;
            return (
              <div
                key={index}
                className="flex items-start justify-between gap-3 py-2"
              >
                <div className="flex min-w-0 gap-2">
                  {logo && (
                    <img
                      src={logo}
                      alt={model.provider ?? ""}
                      className="mt-0.5 size-4 shrink-0 rounded-sm object-contain"
                    />
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-sm">{model.model}</span>
                      {model.purpose && (
                        <Chip className="font-normal">
                          {purposeLabel(model.purpose)}
                        </Chip>
                      )}
                    </div>
                    <div className="mt-0.5 text-[11px] text-muted-foreground">
                      {tokensLabel(
                        model.input_tokens,
                        model.cached_input_tokens,
                        model.output_tokens
                      )}
                    </div>
                  </div>
                </div>
                <span
                  className={cn(
                    "pl-4 text-sm font-medium whitespace-nowrap tabular-nums",
                    isEffectivelyZero(model.cost) &&
                      "font-normal text-muted-foreground/60"
                  )}
                >
                  {`$${model.cost.toFixed(4)}`}
                </span>
              </div>
            );
          })}
          {data.embedding_cost > 0 && (
            <div className="flex items-start justify-between gap-3 py-2">
              <div className="flex min-w-0 gap-2">
                <Database
                  size={16}
                  strokeWidth={1.75}
                  className="mt-0.5 shrink-0 text-muted-foreground"
                />
                <span className="truncate text-sm">Embeddings</span>
              </div>
              <span className="pl-4 text-sm font-medium whitespace-nowrap tabular-nums">
                {`$${data.embedding_cost.toFixed(4)}`}
              </span>
            </div>
          )}
        </div>
      </div>

      {exactTools.length > 0 && (
        <div>
          <SectionTitle>Workflow &amp; agent calls</SectionTitle>
          <div className="divide-y divide-border/60">
            {exactTools.map((tool, index) => (
              <ToolLine key={index} tool={tool} />
            ))}
          </div>
        </div>
      )}

      {estimateTools.length > 0 && (
        <Collapsible className="rounded-md bg-muted/40">
          <CollapsibleTrigger className="group flex w-full items-center gap-1.5 px-2.5 py-2 text-left">
            <ChevronRightIcon className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-90" />
            <span className="text-xs font-medium">Included in model spend</span>
            <span
              className={cn(
                "ml-auto text-[11px] tabular-nums",
                isEffectivelyZero(estimateTotal) && "text-muted-foreground/60"
              )}
            >
              {`~$${estimateTotal.toFixed(4)}`}
            </span>
          </CollapsibleTrigger>
          <CollapsibleContent className="px-2.5 pb-2">
            <p className="pb-1 text-[11px] text-muted-foreground">
              Token usage per tool: already counted in the model calls above,
              not added. These are estimated values.
            </p>
            <div className="divide-y divide-border/60">
              {estimateTools.map((tool, index) => (
                <ToolLine key={index} tool={tool} />
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}

export function ConversationCostSheet({
  isOpen,
  onOpenChange,
  conversationId,
  initialExpandedMessageId,
}: ConversationCostSheetProps) {
  const { spotGroup } = useSpotUser();
  const providerLogos = useProviderLogos(isOpen);
  const [sortMode, setSortMode] = useState<SortMode>("time_desc");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  // The sheet stays mounted between opens, so each open bumps the query key
  // to force a fresh summary — otherwise a reopen after a new turn serves the
  // cached row list without that turn.
  const [openNonce, setOpenNonce] = useState(0);
  const appliedInitialExpand = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setOpenNonce((nonce) => nonce + 1);
    } else {
      appliedInitialExpand.current = false;
      setExpandedIds(new Set());
    }
  }, [isOpen]);

  const { data: summary, isLoading } = useQuery({
    queryKey: ["conversation-cost-summary", conversationId, openNonce],
    queryFn: async () => {
      const response = await ConversationsService.getConversationCostSummary({
        path: {
          group_id: spotGroup.id,
          conversation_id: conversationId!,
        },
      });
      return response.data;
    },
    enabled: isOpen && !!conversationId && !!spotGroup.id,
    retry: false,
  });

  // The $ icon that opened the sheet decides which message starts expanded —
  // applied once per open, so later refetches don't reset manual toggles.
  useEffect(() => {
    if (!isOpen || !summary || appliedInitialExpand.current) return;
    appliedInitialExpand.current = true;
    if (initialExpandedMessageId == null) return;
    const match = summary.messages.find(
      (row) => row.message_id === initialExpandedMessageId
    );
    setExpandedIds(match ? new Set([match.message_id]) : new Set());
  }, [isOpen, initialExpandedMessageId, summary]);

  const rows = useMemo(() => {
    const items = [...(summary?.messages ?? [])];
    if (sortMode === "cost") {
      items.sort((a, b) => b.cost - a.cost);
    } else if (sortMode === "time_desc") {
      items.sort((a, b) => b.created_at.localeCompare(a.created_at));
    }
    return items;
  }, [summary, sortMode]);

  const toggleRow = (messageId: string) => {
    setExpandedIds((previous) => {
      const next = new Set(previous);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  };

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex flex-col gap-0 p-0 sm:max-w-md"
      >
        <SheetHeader className="mx-0 border-b px-6">
          <SheetTitle>Conversation costs</SheetTitle>
        </SheetHeader>
        <div className="flex min-h-0 flex-1 flex-col">
          {isLoading ? (
            <div className="flex flex-col gap-3 px-6 py-4">
              <Skeleton className="h-6 w-1/3" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          ) : summary ? (
            <>
              <div className="shrink-0 px-6 pt-4">
                <div className="grid grid-cols-2 gap-x-4 gap-y-5">
                  <div className="col-span-2">
                    <Stat
                      title="Total cost"
                      value={`$${summary.total_cost.toFixed(4)}`}
                    />
                  </div>
                  <Stat
                    title="Input tokens"
                    value={summary.input_tokens.toLocaleString()}
                    footer={
                      summary.cached_input_tokens > 0 ? (
                        <span className="text-xs text-emerald-600 dark:text-emerald-400">
                          {`${summary.cached_input_tokens.toLocaleString()} cached`}
                        </span>
                      ) : undefined
                    }
                  />
                  <Stat
                    title="Output tokens"
                    value={summary.output_tokens.toLocaleString()}
                  />
                </div>

                <div className="-mx-6 mt-6 flex items-center justify-between border-t border-border px-6 pt-4">
                  <SectionTitle>
                    <span className="text-sm font-medium">Messages</span>
                  </SectionTitle>
                  <Select
                    value={sortMode}
                    onValueChange={(value) => setSortMode(value as SortMode)}
                  >
                    <SelectTrigger size="sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent align="end">
                      <SelectItem value="time_desc">Newest first</SelectItem>
                      <SelectItem value="time">Oldest first</SelectItem>
                      <SelectItem value="cost">Order by cost</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-auto">
                <div className="mt-1.5 pb-4">
                  {rows.map((row, index) => (
                    <Fragment key={row.message_id}>
                      {index > 0 && (
                        <div className="mx-4 border-t border-border" />
                      )}
                      <Collapsible
                        open={expandedIds.has(row.message_id)}
                        onOpenChange={() => toggleRow(row.message_id)}
                      >
                        <CollapsibleTrigger className="flex w-full min-w-0 items-center gap-2 px-6 py-2.5 text-left">
                          <ChevronRightIcon
                            className={cn(
                              "size-4 shrink-0 text-muted-foreground transition-transform",
                              expandedIds.has(row.message_id) && "rotate-90"
                            )}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm">
                              {row.preview || "Untitled message"}
                            </div>
                            <div className="mt-0.5 text-[11px] text-muted-foreground">
                              {rowMeta(row)}
                            </div>
                          </div>
                          <span
                            className={cn(
                              "pl-4 text-sm whitespace-nowrap tabular-nums",
                              !isEffectivelyZero(row.cost)
                                ? "font-medium"
                                : "font-normal text-muted-foreground/60"
                            )}
                          >
                            {`$${row.cost.toFixed(4)}`}
                          </span>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          {conversationId && (
                            <MessageBreakdownDetail
                              conversationId={conversationId}
                              messageId={row.message_id}
                              providerLogos={providerLogos}
                            />
                          )}
                        </CollapsibleContent>
                      </Collapsible>
                    </Fragment>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="px-6 py-4 text-sm text-muted-foreground">
              No cost data available.
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
